import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../utils/api';

const COLORS = [
  '#6366f1','#10b981','#f59e0b','#ef4444','#3b82f6','#8b5cf6','#ec4899','#14b8a6',
];

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'code-block'],
    ['clean'],
  ],
};

function strip(html) {
  return html ? html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : '';
}

function timeAgo(dateStr) {
  const d = new Date(dateStr);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'только что';
  if (min < 60) return min + ' мин. назад';
  const h = Math.floor(min / 60);
  if (h < 24) return h + ' ч. назад';
  const days = Math.floor(h / 24);
  if (days < 7) return days + ' дн. назад';
  return d.toLocaleDateString('ru-RU');
}

export default function UsefulPage() {
  const [notes, setNotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [search, setSearch] = useState('');
  const [activeNote, setActiveNote] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', category_id: '' });
  const [catModal, setCatModal] = useState(false);
  const [catForm, setCatForm] = useState({ name: '', color: '#6366f1' });
  const [editCat, setEditCat] = useState(null);
  const searchRef = useRef(null);

  const loadCategories = useCallback(async () => {
    const { data } = await api.get('/useful/categories');
    setCategories(data);
  }, []);

  const loadNotes = useCallback(async () => {
    const params = {};
    if (selectedCat) params.category_id = selectedCat;
    if (search) params.q = search;
    const { data } = await api.get('/useful/notes', { params });
    setNotes(data);
  }, [selectedCat, search]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadNotes(); }, [loadNotes]);

  const openNew = () => {
    setActiveNote(null);
    setForm({ title: '', content: '', category_id: selectedCat || '' });
    setEditing(true);
  };

  const openNote = (note) => {
    setActiveNote(note);
    setForm({ title: note.title, content: note.content || '', category_id: note.category_id || '' });
    setEditing(false);
  };

  const saveNote = async () => {
    if (!form.title.trim()) return;
    if (activeNote) {
      const { data } = await api.put('/useful/notes/' + activeNote.id, form);
      setActiveNote(data);
    } else {
      const { data } = await api.post('/useful/notes', form);
      setActiveNote(data);
    }
    setEditing(false);
    loadNotes();
  };

  const deleteNote = async (id) => {
    if (!window.confirm('Удалить заметку?')) return;
    await api.delete('/useful/notes/' + id);
    setActiveNote(null);
    setEditing(false);
    loadNotes();
  };

  const saveCat = async () => {
    if (!catForm.name.trim()) return;
    try {
      if (editCat) {
        await api.put('/useful/categories/' + editCat.id, catForm);
      } else {
        await api.post('/useful/categories', catForm);
      }
      setCatModal(false);
      setEditCat(null);
      setCatForm({ name: '', color: '#6366f1' });
      loadCategories();
      loadNotes();
    } catch (err) {
      alert('Ошибка сохранения: ' + (err.response?.data?.error || err.message));
    }
  };

  const deleteCat = async (id) => {
    if (!window.confirm('Удалить категорию? Заметки не удалятся.')) return;
    await api.delete('/useful/categories/' + id);
    if (selectedCat === id) setSelectedCat(null);
    loadCategories();
    loadNotes();
  };

  const cat = categories.find(c => c.id === (activeNote?.category_id));

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', overflow: 'hidden', background: '#f8fafc' }}>

      {/* Колонка 1 — Категории */}
      <div style={{ width: 200, minWidth: 180, display: 'flex', flexDirection: 'column', borderRight: '1px solid #e2e8f0', background: '#fff' }}>
        <div style={{ padding: '14px 14px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>Категории</span>
          <button
            onClick={() => { setEditCat(null); setCatForm({ name: '', color: '#6366f1' }); setCatModal(true); }}
            style={{ fontSize: 18, lineHeight: 1, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px' }}
            title="Добавить категорию"
          >+</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div
            onClick={() => setSelectedCat(null)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', cursor: 'pointer', background: !selectedCat ? '#eef2ff' : 'transparent', borderLeft: !selectedCat ? '3px solid #6366f1' : '3px solid transparent' }}
          >
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#6366f1', flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: !selectedCat ? '#4338ca' : '#475569', fontWeight: !selectedCat ? 600 : 400 }}>Все заметки</span>
          </div>
          {categories.map(c => (
            <div
              key={c.id}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', cursor: 'pointer', background: selectedCat === c.id ? '#f8fafc' : 'transparent', borderLeft: selectedCat === c.id ? '3px solid ' + c.color : '3px solid transparent' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }} onClick={() => setSelectedCat(selectedCat === c.id ? null : c.id)}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.color, flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: selectedCat === c.id ? '#1e293b' : '#475569', fontWeight: selectedCat === c.id ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
              </div>
              <button
                onClick={e => { e.stopPropagation(); setEditCat(c); setCatForm({ name: c.name, color: c.color }); setCatModal(true); }}
                style={{ fontSize: 11, color: '#cbd5e1', background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', flexShrink: 0 }}
                title="Редактировать"
              >✎</button>
            </div>
          ))}
        </div>
        <div style={{ padding: '8px 14px 12px' }}>
          <button onClick={openNew} style={{ width: '100%', padding: '8px', borderRadius: 8, background: '#6366f1', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            + Заметка
          </button>
        </div>
      </div>

      {/* Колонка 2 — Список заметок */}
      <div style={{ width: 260, minWidth: 220, display: 'flex', flexDirection: 'column', borderRight: '1px solid #e2e8f0', background: '#fff' }}>
        <div style={{ padding: '14px 14px 10px' }}>
          <input
            ref={searchRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Поиск..."
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {notes.length === 0 && (
            <div style={{ padding: 20, color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>
              {search ? 'Ничего не найдено' : 'Заметок пока нет'}
            </div>
          )}
          {notes.map(note => {
            const isActive = activeNote?.id === note.id;
            const preview = strip(note.content).slice(0, 80);
            return (
              <div
                key={note.id}
                onClick={() => openNote(note)}
                style={{
                  padding: '12px 14px',
                  borderBottom: '1px solid #f1f5f9',
                  cursor: 'pointer',
                  background: isActive ? '#eef2ff' : 'transparent',
                  borderLeft: isActive ? '3px solid #6366f1' : '3px solid transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  {note.category_color && (
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: note.category_color, flexShrink: 0 }} />
                  )}
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{note.title}</div>
                </div>
                {preview && <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preview}</div>}
                <div style={{ fontSize: 10, color: '#94a3b8' }}>{timeAgo(note.updated_at)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Правая панель — заметка */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {(!activeNote && !editing) ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 48, color: "var(--text-muted)" }}>—</div>
            <div style={{ fontSize: 15 }}>Выберите заметку или создайте новую</div>
          </div>
        ) : editing ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 24, gap: 12, overflow: 'hidden' }}>
            {/* Заголовок */}
            <input
              value={form.title}
              onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="Заголовок..."
              style={{ fontSize: 22, fontWeight: 700, border: 'none', outline: 'none', background: 'transparent', color: '#1e293b', width: '100%' }}
            />
            {/* Категория */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <select
                value={form.category_id || ''}
                onChange={e => setForm(p => ({ ...p, category_id: e.target.value ? Number(e.target.value) : null }))}
                style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', color: '#475569' }}
              >
                <option value="">Без категории</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <div style={{ flex: 1 }} />
              <button onClick={saveNote} style={{ padding: '6px 18px', borderRadius: 8, background: '#6366f1', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Сохранить</button>
              <button onClick={() => { setEditing(false); if (!activeNote) setActiveNote(null); }} style={{ padding: '6px 14px', borderRadius: 8, background: '#f1f5f9', color: '#475569', border: 'none', cursor: 'pointer', fontSize: 13 }}>Отмена</button>
            </div>
            {/* Редактор */}
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <ReactQuill
                value={form.content}
                onChange={val => setForm(p => ({ ...p, content: val }))}
                modules={quillModules}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}
                theme="snow"
              />
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Шапка заметки */}
            <div style={{ padding: '20px 24px 12px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1e293b' }}>{activeNote.title}</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                  {cat && (
                    <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20, background: cat.color + '22', color: cat.color, fontWeight: 600 }}>{cat.name}</span>
                  )}
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>Изменено {timeAgo(activeNote.updated_at)}</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>Создано {new Date(activeNote.created_at).toLocaleDateString('ru-RU')}</span>
                </div>
              </div>
              <button onClick={() => { setForm({ title: activeNote.title, content: activeNote.content || '', category_id: activeNote.category_id || '' }); setEditing(true); }} style={{ padding: '6px 16px', borderRadius: 8, background: '#f1f5f9', color: '#475569', border: 'none', cursor: 'pointer', fontSize: 13 }}>Редактировать</button>
              <button onClick={() => deleteNote(activeNote.id)} style={{ padding: '6px 14px', borderRadius: 8, background: '#fee2e2', color: '#b91c1c', border: 'none', cursor: 'pointer', fontSize: 13 }}>Удал.</button>
            </div>
            {/* Контент */}
            <div
              className="ql-editor"
              style={{ flex: 1, overflowY: 'auto', padding: '20px 28px', fontSize: 14, lineHeight: 1.7, color: '#1e293b' }}
              dangerouslySetInnerHTML={{ __html: activeNote.content || '<p style="color:#94a3b8">Пусто</p>' }}
            />
          </div>
        )}
      </div>

      {/* Модалка категории */}
      {catModal && (
        <div style={{ position: 'fixed', inset: 0, background: '#00000055', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 360, boxShadow: '0 20px 60px #0002' }}>
            <h3 style={{ margin: '0 0 18px', fontSize: 16 }}>{editCat ? 'Редактировать категорию' : 'Новая категория'}</h3>
            <input
              value={catForm.name}
              onChange={e => setCatForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Название..."
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, marginBottom: 14, boxSizing: 'border-box' }}
            />
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Цвет</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {COLORS.map(col => (
                  <div
                    key={col}
                    onClick={() => setCatForm(p => ({ ...p, color: col }))}
                    style={{ width: 28, height: 28, borderRadius: '50%', background: col, cursor: 'pointer', border: catForm.color === col ? '3px solid #1e293b' : '3px solid transparent' }}
                  />
                ))}
              </div>
            </div>
            {editCat && (
              <div style={{ marginBottom: 14 }}>
                <button onClick={() => deleteCat(editCat.id)} style={{ fontSize: 12, color: '#b91c1c', background: 'none', border: 'none', cursor: 'pointer' }}>Удал. Удалить категорию</button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={saveCat} style={{ flex: 1, padding: '9px', borderRadius: 8, background: '#6366f1', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>Сохранить</button>
              <button onClick={() => { setCatModal(false); setEditCat(null); }} style={{ flex: 1, padding: '9px', borderRadius: 8, background: '#f1f5f9', color: '#475569', border: 'none', cursor: 'pointer' }}>Отмена</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
